import React from 'react';
import { SelectOption } from 'global';
import { gql, useLazyQuery } from '@apollo/client';
import { useFieldArray, useForm } from 'react-hook-form';
import { AccountType } from '@trnsact/trnsact-shared-types';
import { Box, Button, Stack, Typography } from '@mui/material';
import { AsyncAutocompleteField, CheckboxField } from 'components/form';

import { capitalizeFirstLetter } from '../../../../formatters';
import { ExternalDashboardsPortalConfigFormValues } from '../types';

interface Props {
  isLoading: boolean;
  externalDashboardOptions: SelectOption[];
  initialValue: ExternalDashboardsPortalConfigFormValues;
  onConfirm: (configuration: ExternalDashboardsPortalConfigFormValues) => Promise<void>;
}

const GET_ACCOUNTS = gql`
  query ($accountsNameFilter: String, $isVPAccountsOnly: Boolean) {
    accounts(accountsNameFilter: $accountsNameFilter, isVPAccountsOnly: $isVPAccountsOnly) {
      value: id
      label: name
    }
  }
`;

export const ExternalDashboardForm = ({ isLoading, onConfirm, externalDashboardOptions, initialValue }: Props) => {
  const [getAccounts] = useLazyQuery(GET_ACCOUNTS);

  const { control, handleSubmit, setValue } = useForm<ExternalDashboardsPortalConfigFormValues>({
    defaultValues: initialValue.dashboards.length
      ? initialValue
      : {
          dashboards: externalDashboardOptions.map(({ value }) => ({
            accounts: [],
            dashboardId: value,
            accountTypes: {
              PARTNER: false,
              [AccountType.Dealer]: false,
              [AccountType.Lender]: false,
            },
          })),
        },
  });

  const { fields } = useFieldArray({ control, name: 'dashboards' });

  const externalDashboardMap = externalDashboardOptions.reduce<Record<string, string>>((acc, { value, label }) => {
    acc[value] = label;
    return acc;
  }, {});

  return (
    <Box sx={{ display: 'grid', gridTemplateRows: '1fr auto', gap: '1rem', height: '100%' }}>
      <Stack gap="0.5rem">
        {fields.map((field, index) => (
          <Stack key={field.id} gap="0.5rem">
            <Typography variant="h6" component="span">
              Dashboard: {externalDashboardMap[field.dashboardId]}
            </Typography>

            <Stack gap="1rem">
              <Stack direction="row">
                <CheckboxField
                  control={control}
                  name={`dashboards.${index}.accountTypes.${AccountType.Dealer}`}
                  label={`Full access for ${capitalizeFirstLetter(AccountType.Dealer)}s`}
                  extraAction={() => {
                    setValue(`dashboards.${index}.accounts`, []);
                  }}
                />

                <CheckboxField
                  control={control}
                  name={`dashboards.${index}.accountTypes.${AccountType.Lender}`}
                  label={`Full access for ${capitalizeFirstLetter(AccountType.Lender)}s`}
                  extraAction={() => {
                    setValue(`dashboards.${index}.accounts`, []);
                  }}
                />

                <CheckboxField
                  control={control}
                  label={`Full access for Partners`}
                  name={`dashboards.${index}.accountTypes.PARTNER`}
                  extraAction={() => {
                    setValue(`dashboards.${index}.accounts`, []);
                  }}
                />
              </Stack>

              <AsyncAutocompleteField
                multiple
                control={control}
                name={`dashboards.${index}.accounts`}
                label="Dashboard available for accounts:"
                extraAction={() => {
                  setValue(`dashboards.${index}.accountTypes.PARTNER`, false);
                  setValue(`dashboards.${index}.accountTypes.${AccountType.Dealer}`, false);
                  setValue(`dashboards.${index}.accountTypes.${AccountType.Lender}`, false);
                }}
                fetchOptions={async search => {
                  const { data } = await getAccounts({
                    variables: { accountsNameFilter: search, isVPAccountsOnly: true },
                  });

                  return data?.accounts ?? [];
                }}
              />
            </Stack>
          </Stack>
        ))}
      </Stack>

      <Box>
        <Button loading={isLoading} variant="contained" onClick={handleSubmit(onConfirm)} size="small">
          Save
        </Button>
      </Box>
    </Box>
  );
};
